import { TDate } from '@api/io/session.io';
import {
  ContextPagination as Pagination,
  usePaginationPageSizes,
} from '@components/ContextPagination';
import { EmptyStateWrapper } from '@fe-learner-common/components/EmptyState';
import { faUser } from '@fortawesome/pro-solid-svg-icons/faUser';
import { usePageParam } from '@hooks/params/usePageParam';
import { TabPage } from '@routes/session-details/components/TabPage';
import { TW_ROOT } from '@utils/constants';
import { FaIcon, Sorter } from 'fe-learner-common';
import { FC, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { EventRow } from './EventRow';
import { PaginationPageSize } from '@fe-core-ui/src/components/Pagination/PageSizeSelect';

type EventsTabProps = {
  dates?: TDate[] | null;
};

type SortOrder = 'date-asc' | 'date-desc';

export const EventsTab: FC<EventsTabProps> = ({ dates }) => {
  const intl = useIntl();
  const [sortOrderKey, setSortOrderKey] = useState<SortOrder>('date-asc');
  const [pageSize, setPageSize] = useState<PaginationPageSize>(4);
  const { page, setPage } = usePageParam();
  const pageIndex = page - 1;
  const totalNoItems = dates?.length;
  const pageSizeItems = usePaginationPageSizes([{ key: 4, label: '4' }]);
  const sortOptions: Array<{ key: SortOrder; label: string }> = [
    {
      key: 'date-asc',
      label: intl.formatMessage({ id: 'catalog.tab.events.sortBy.date.asc' }),
    },
    {
      key: 'date-desc',
      label: intl.formatMessage({
        id: 'catalog.tab.events.sortBy.date.desc',
      }),
    },
  ];

  const actions =
    totalNoItems && totalNoItems > 0 ? (
      <Sorter
        container={document.getElementById(TW_ROOT) as HTMLElement}
        items={sortOptions}
        selectedSortOrderKey={sortOrderKey}
        onSelect={(sortOrder) => setSortOrderKey(sortOrder.key as SortOrder)}
        triggerSize="sm"
      />
    ) : null;

  const events = useMemo(() => {
    if (!dates || !dates?.length) {
      return [];
    }

    const sorted = sortDates(dates, sortOrderKey);
    const calculatedPageSize =
      pageSize === 'all' ? totalNoItems || 0 : pageSize;
    return sorted.slice(
      pageIndex * calculatedPageSize,
      (pageIndex + 1) * calculatedPageSize,
    );
  }, [dates, pageIndex, pageSize, sortOrderKey, totalNoItems]);

  return (
    <TabPage
      title={intl.formatMessage({ id: 'catalog.tab.events.title' })}
      actions={actions}
    >
      <EmptyStateWrapper
        icon={<FaIcon icon={faUser} className="text-neutral-550" />}
        label={intl.formatMessage({
          id: 'catalog.tab.events.empty.label',
        })}
        description={intl.formatMessage({
          id: 'catalog.tab.events.empty.description',
        })}
        condition={!!dates?.length}
      >
        <ul className="flex flex-col gap-2">
          {events
            ? events.map((date) => (
                <EventRow key={`event-list-${date.id}`} date={date} />
              ))
            : null}
        </ul>
      </EmptyStateWrapper>
      <Pagination
        type="link"
        actionContext="Events"
        className="pb-3 pt-4 lg:px-4"
        page={page}
        totalNoItems={totalNoItems ?? 0}
        pageSizeOptions={{
          options: pageSizeItems,
          pageSize,
          onPageSizeChange: (pageSize) => {
            setPageSize(pageSize);
          },
        }}
        setPageParams={(page) => {
          setPage(page.toString());
        }}
        texts={{
          ariaLabel: intl.formatMessage({
            id: 'catalog.tab.events.pagination.ariaLabel',
          }),
        }}
      />
    </TabPage>
  );
};

const sortDates = (dates: TDate[], sortOrderKey: SortOrder) =>
  dates.sort((a, b) => {
    if (!a.starts || !b.starts) {
      return 0;
    }

    const aTime = new Date(a.starts).getTime();
    const bTime = new Date(b.starts).getTime();

    if (sortOrderKey === 'date-asc') {
      return aTime - bTime;
    }

    return bTime - aTime;
  });
